import { ComponentType, JSX, ReactNode } from 'react';

import {
  ApiBookedItem,
  ApiBooking,
  ApiComponent,
  ApiFlightItem,
  ApiItem,
  ApiItemType,
  ApiPrice
} from '@ibe/api';
import PackageComponentOptions from './PackageComponentOptions';
import deepmerge from 'deepmerge';

export enum ItemComponentView {
  SUMMARY = 'Summary',
  CART = 'Cart',
  DETAIL = 'Detail',
  LIST = 'List',
  CHECKOUT = 'Checkout'
}

type DifferentComponentType =
  | 'Base'
  | 'Extra'
  | 'Flight'
  | 'Bus'
  | 'Hotel'
  | 'Package'
  | 'RentalCar'
  | 'Transfer';

export type SummaryOrderType = {
  [key in DifferentComponentType]?: SummaryOrderKeyType[];
};

export type SummaryOrderKeyType =
  | 'TITLE'
  | 'TITLE_DELETE'
  | 'DELETE'
  | 'SUBTITLE'
  | 'DATE'
  | 'DESCRIPTION'
  | 'ADDITIONAL_INFO'
  | 'PRICE_AND_ICON'
  | 'ICON'
  | 'PRICE'
  | 'NUMBER_OF_PERSON';

export const defaultSummaryOrder: SummaryOrderKeyType[] = [
  'TITLE_DELETE',
  'SUBTITLE',
  'DATE',
  'DESCRIPTION',
  'ADDITIONAL_INFO',
  'NUMBER_OF_PERSON',
  'PRICE_AND_ICON'
];

export interface ComponentBoxProps {
  component: ApiComponent;
  onModifyComponent?: (componentId: string, options?: PackageComponentOptions) => void;
  onRemoveComponent?: (componentId: string, itemId: string) => void;
}

export interface ItemComponentProps {
  item: ApiItem;
}

export type ExtendedApiBookedItem = ApiBookedItem & { useValuePriceDisplay?: boolean };

export type BookedItemComponentProps = {
  booking: ApiBooking;
  item: ExtendedApiBookedItem;
  relatedItems?: ApiBookedItem[];
  isOpen?: boolean;
  showTraveler?: boolean;
  showTravelerSeparate?: boolean;
  travelerId?: string;
  showPromoCodeDiscount?: boolean;
  showInfo?: boolean;
  showSsr?: boolean;
  showSeats?: boolean;
  showAdditionalInfo?: boolean;
  children?: ReactNode;
  onDelete?: (idItem: string) => Promise<void>;
  onEdit?: (idItem: string) => Promise<void>;
  withIconColumn?: boolean;
  hideSinglePrices?: boolean;
  customFlightDateDisplay?: (a: ApiFlightItem) => JSX.Element;
  customPriceModifier?: (a: ApiBookedItem, id?: string) => ApiPrice | undefined;
  showNumberOfPerson?: boolean;
  order?: SummaryOrderType;
  displayExtraServiceDate?: (item: ApiBookedItem) => boolean;
  displayExtraServiceTime?: (item: ApiBookedItem) => boolean;
  displayRelatedItems?: (item: ApiBookedItem) => boolean;
  showPax?: boolean;
  useCustomItemOrder?: boolean;
  renderExternalContent?: JSX.Element;
  useNameForDescription?: boolean;
  customLabelsMapper?: (item: ApiBookedItem) => ReactNode;
  showPricePlusMinus?: boolean;
  isInEditMode?: boolean;
};

export type ItemRegistry = Partial<
  Record<
    ApiItemType,
    {
      PackageComponentBox: ComponentType<ComponentBoxProps>;
      List: ComponentType<ItemComponentProps>;
      Detail: ComponentType<ItemComponentProps>;
      Cart: ComponentType<BookedItemComponentProps>;
      Summary: ComponentType<BookedItemComponentProps>;
      Checkout: ComponentType<BookedItemComponentProps>;
    }
  >
>;

export type ItemTypeRepresentations = {
  PackageComponentBox?: ComponentType<ComponentBoxProps>;
  List?: ComponentType<ItemComponentProps>;
  Detail?: ComponentType<ItemComponentProps>;
  Cart?: ComponentType<BookedItemComponentProps>;
  Summary?: ComponentType<BookedItemComponentProps>;
  Checkout?: ComponentType<BookedItemComponentProps>;
};

export type CustomItemRegistry = Partial<
  Record<
    ApiItemType,
    {
      PackageComponentBox?: ComponentType<ComponentBoxProps>;
      List?: ComponentType<ItemComponentProps>;
      Detail?: ComponentType<ItemComponentProps>;
      Cart?: ComponentType<BookedItemComponentProps>;
      Summary?: ComponentType<BookedItemComponentProps>;
      Checkout?: ComponentType<BookedItemComponentProps>;
    }
  >
>;

export function mergeItemRegisters(
  defaultItemRegistry: ItemRegistry,
  customItemRegistry: CustomItemRegistry
): ItemRegistry {
  return deepmerge(defaultItemRegistry, customItemRegistry) as ItemRegistry;
}
